/**
 * Processing fee (surcharge) calculation and surcharge eligibility.
 * Used when passing Stripe card processing costs to students; shared by backend, web, and landing.
 * No Stripe SDK dependency—domain logic only.
 */

import { MARKET_CONFIG } from '../constants/markets';
import type { Region } from '../types/region';

/** Stripe standard rates: percentage (0.029 = 2.9%) + fixed amount in dollars */
export interface ProcessingFeeRate {
  percentage: number;
  fixed: number;
}

/** US: 2.9% + $0.30 (Stripe US standard). */
export const STRIPE_RATE_US: ProcessingFeeRate = {
  percentage: 0.029,
  fixed: 0.3,
};

/** AU domestic cards: 1.65% + A$0.30 (Stripe AU domestic, effective 1 Oct 2026). */
export const STRIPE_RATE_AU_DOMESTIC: ProcessingFeeRate = {
  percentage: 0.0165,
  fixed: 0.3,
};

/** Date from which AU card surcharges are restricted under RBA rules (ISO date). */
export const AU_CARD_SURCHARGE_RESTRICTED_FROM = '2026-10-01';

/** AU international cards: 3.5% + A$0.30 (reference; academy absorbs difference if student uses international). */
export const STRIPE_RATE_AU_INTERNATIONAL: ProcessingFeeRate = {
  percentage: 0.035,
  fixed: 0.3,
};

/** US states where credit card surcharging is prohibited (two-letter uppercase). */
export const US_STATES_SURCHARGE_PROHIBITED: readonly string[] = ['CT', 'MA'] as const;

/** Disclosure shown when academies enable pass-through surcharges. */
export const PROCESSING_TAX_DISCLOSURE =
  'Displayed processing fees exclude any tax Stripe may charge on its own fees.';

export interface SurchargeEligibility {
  allowed: boolean;
  reason?: string;
}

/**
 * Whether surcharging (passing processing fee to customer) is allowed for the given location.
 * AU: not allowed under RBA card surcharge rules effective 1 Oct 2026.
 * US: not allowed in CT, MA; otherwise allowed. stateOrProvince is two-letter (e.g. "CT", "MA").
 */
export function isSurchargeAllowed(
  region: Region,
  stateOrProvince?: string | null
): SurchargeEligibility {
  if (MARKET_CONFIG[region].surchargePolicy === 'not_supported') {
    return {
      allowed: false,
      reason: 'Surcharges are not supported in this market.',
    };
  }
  if (region === 'AU') {
    return {
      allowed: false,
      reason:
        'Card surcharges are not supported for Australian payments under RBA card surcharge rules effective 1 October 2026.',
    };
  }
  if (region === 'US') {
    const state = stateOrProvince?.trim().toUpperCase();
    if (state && US_STATES_SURCHARGE_PROHIBITED.includes(state)) {
      return {
        allowed: false,
        reason: `Surcharges are not permitted in ${state}.`,
      };
    }
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: 'Surcharge rules are not configured for this market.',
  };
}

/**
 * Calculate processing fee (surcharge) in dollars so that after Stripe takes their fee
 * from the total (subtotal + surcharge), the academy receives exactly the subtotal.
 * Stripe's fee is applied to the total: total * percentage + fixed.
 * So: net = total - (total * p + f) = total * (1 - p) - f. We want net = subtotal.
 * Therefore: total = (subtotal + f) / (1 - p), and surcharge = total - subtotal.
 * US: 2.9% + $0.30. AU: surcharges not allowed (returns 0). Rounded to 2 decimal places.
 */
export function calculateProcessingFee(amountInDollars: number, region: Region): number {
  if (amountInDollars <= 0) return 0;
  if (region === 'AU' || !isSurchargeAllowed(region).allowed) return 0;
  const rate = STRIPE_RATE_US;
  const totalToCharge = (amountInDollars + rate.fixed) / (1 - rate.percentage);
  const surcharge = totalToCharge - amountInDollars;
  return Math.round(surcharge * 100) / 100;
}

/**
 * Warning information for surcharge pass-through toggle in settings UI.
 * Used to inform academy owners of potential compliance issues before enabling.
 */
export interface SurchargeWarning {
  level: 'info' | 'warning';
  title: string;
  message: string;
}

/**
 * Get contextual warning for surcharge pass-through based on region/state.
 * CT and MA prohibit surcharges (warning level).
 * All other locations get an info-level disclaimer.
 */
export function getSurchargeWarning(
  region: Region,
  stateOrProvince?: string | null
): SurchargeWarning {
  if (region === 'AU') {
    return {
      level: 'warning',
      title: 'Card Surcharges Not Supported in Australia',
      message:
        'CourtSync does not support separate card surcharges on Australian payments under RBA card surcharge rules effective 1 October 2026. Organizations should absorb card processing costs in their listed prices.',
    };
  }

  if (region === 'US') {
    const state = stateOrProvince?.trim().toUpperCase();
    if (state && US_STATES_SURCHARGE_PROHIBITED.includes(state)) {
      return {
        level: 'warning',
        title: `Surcharges Prohibited in ${state}`,
        message: `Credit card surcharging is not permitted in ${state}. Enabling this setting may violate local regulations.`,
      };
    }
  }

  const eligibility = isSurchargeAllowed(region, stateOrProvince);
  if (!eligibility.allowed) {
    return {
      level: 'warning',
      title: 'Surcharges Not Supported',
      message: eligibility.reason ?? 'Credit card surcharges are not supported in this market.',
    };
  }

  return {
    level: 'info',
    title: 'Verify Local Regulations',
    message:
      'Credit card surcharging is regulated differently by jurisdiction. You are responsible for ensuring compliance with the laws and regulations that apply in your location.',
  };
}

/**
 * Acknowledgment text required before enabling surcharge pass-through.
 */
export const SURCHARGE_ACKNOWLEDGMENT_TEXT =
  'I confirm that I have reviewed my local regulations regarding credit card surcharges and accept responsibility for compliance.';
